/**
 * Destinations for the frame, role-filtered. One list drives the sidebar,
 * the tab bar and its More sheet, so a role never sees a destination in one
 * place and not another.
 */

import {
  BookOpen,
  CalendarClock,
  CalendarRange,
  ClipboardList,
  DatabaseZap,
  FileText,
  FolderOpen,
  GraduationCap,
  KanbanSquare,
  LayoutDashboard,
  LifeBuoy,
  Megaphone,
  MessagesSquare,
  PoundSterling,
  TrendingUp,
  Users,
  type LucideIcon,
} from "lucide-react";

import type { UserRole } from "@/lib/api/auth";
import type { FeatureName } from "@/lib/api/tenant";
import { ROUTES } from "@/types/routes";

export interface NavDestination {
  to: string;
  label: string;
  icon: LucideIcon;
  roles?: UserRole[];
  group: "work" | "people" | "admin";
  /** Single-key shortcut used after `g`, shown in the shortcut sheet. */
  key: string;
  /** Hidden (and its route redirects home) when the tenant has this feature off. */
  feature?: FeatureName;
}

export const NAV: NavDestination[] = [
  { to: ROUTES.DASHBOARD, label: "Dashboard", icon: LayoutDashboard, group: "work", key: "d" },
  { to: ROUTES.LESSONS, label: "Lessons", icon: CalendarRange, group: "work", key: "l" },
  { to: ROUTES.HOMEWORK, label: "Homework", icon: BookOpen, group: "work", key: "h" },
  { to: ROUTES.PROGRESS, label: "Progress", icon: TrendingUp, group: "work", key: "p" },
  { to: ROUTES.REPORTS, label: "Reports", icon: FileText, group: "work", key: "o" },
  // Admin only: the bookings and careers endpoints refuse every other role.
  { to: ROUTES.SUBMISSIONS, label: "Submissions", icon: ClipboardList, roles: ["admin"], group: "work", key: "b" },
  { to: ROUTES.PIPELINE, label: "Enquiries", icon: KanbanSquare, roles: ["admin"], group: "work", key: "e" },
  { to: ROUTES.RESOURCES, label: "Resources", icon: FolderOpen, group: "work", key: "r" },
  { to: ROUTES.MESSAGES, label: "Messages", icon: MessagesSquare, group: "work", key: "m" },
  // Families see their own balance and invoices; the DrTutor team runs billing.
  { to: ROUTES.BILLING, label: "Billing", icon: PoundSterling, roles: ["admin", "parent"], group: "work", key: "i" },
  // Every role: each person sees the updates meant for them; admins see all.
  { to: ROUTES.WHATS_NEW, label: "What's New", icon: Megaphone, group: "work", key: "w" },
  // Every role: the help queries you sent; admins see everyone's.
  { to: ROUTES.CHANGELOG, label: "Changelog", icon: LifeBuoy, group: "work", key: "c", feature: "loopdesk_support" },
  { to: ROUTES.STUDENTS, label: "Students", icon: GraduationCap, roles: ["admin", "tutor", "parent"], group: "people", key: "s" },
  { to: ROUTES.TUTORS, label: "Tutors", icon: Users, roles: ["admin"], group: "people", key: "t" },
  // Assessment availability, Google status and the recordings audit.
  { to: ROUTES.AVAILABILITY, label: "Availability", icon: CalendarClock, roles: ["admin"], group: "admin", key: "a" },
  { to: ROUTES.DATA_CONTROL, label: "Data control", icon: DatabaseZap, roles: ["admin"], group: "admin", key: "x" },
];

export const GROUP_LABEL: Record<NavDestination["group"], string> = {
  work: "Workspace",
  people: "People",
  admin: "Admin",
};

export type TenantFeatures = Record<FeatureName, boolean>;

export function visibleNav(role: UserRole | undefined, features?: TenantFeatures): NavDestination[] {
  return NAV.filter(
    (n) =>
      (!n.roles || (role !== undefined && n.roles.includes(role))) &&
      (!n.feature || features === undefined || features[n.feature]),
  );
}

/** Parents see their children under Students, so the label says so. */
export function labelFor(item: NavDestination, role: UserRole | undefined): string {
  if (item.to === ROUTES.STUDENTS && role === "parent") return "My children";
  return item.label;
}

/** The four tabs each role gets in the bottom bar below 1024px, in order.
 *  Everything else lives in the More sheet (`12-MOBILE-APP.md`). */
const TABS: Record<UserRole, string[]> = {
  admin: [ROUTES.DASHBOARD, ROUTES.LESSONS, ROUTES.STUDENTS, ROUTES.MESSAGES],
  tutor: [ROUTES.DASHBOARD, ROUTES.LESSONS, ROUTES.HOMEWORK, ROUTES.STUDENTS],
  student: [ROUTES.DASHBOARD, ROUTES.LESSONS, ROUTES.HOMEWORK, ROUTES.PROGRESS],
  parent: [ROUTES.DASHBOARD, ROUTES.LESSONS, ROUTES.STUDENTS, ROUTES.MESSAGES],
};

export function tabsFor(role: UserRole | undefined, features?: TenantFeatures): { tabs: NavDestination[]; more: NavDestination[] } {
  const items = visibleNav(role, features);
  const picks = role ? TABS[role] : [ROUTES.DASHBOARD];
  const tabs = picks.map((to) => items.find((i) => i.to === to)).filter((i): i is NavDestination => Boolean(i));
  return { tabs, more: items.filter((i) => !tabs.includes(i)) };
}

/** The short label under a tab icon. */
export function tabLabelFor(item: NavDestination, role: UserRole | undefined): string {
  if (item.to === ROUTES.DASHBOARD) return "Home";
  return labelFor(item, role);
}

export function isActivePath(pathname: string, to: string): boolean {
  if (to === ROUTES.DASHBOARD) return pathname === "/";
  return pathname === to || pathname.startsWith(`${to}/`);
}

const EXTRA_TITLES: Record<string, string> = {
  [ROUTES.PROFILE]: "Profile",
  [ROUTES.CHANGE_PASSWORD]: "Change password",
  [ROUTES.SETTINGS]: "Settings",
  [ROUTES.SETUP]: "Set up your workspace",
};

export function titleFor(pathname: string, role: UserRole | undefined): string {
  const hit = NAV.find((n) => isActivePath(pathname, n.to));
  if (hit) return labelFor(hit, role);
  if (pathname.startsWith(`${ROUTES.SETTINGS}/`)) return "Settings";
  return EXTRA_TITLES[pathname] ?? "Workspace";
}
